/**
 * Routing rule for the signed-note appointment lock.
 *
 * The lock itself is enforced by the schedule-appointment bot (it can trust the
 * caller identity). Direct FHIR writes from the browser cannot, so any appointment
 * that carries a clinical note must be updated through the bot.
 */

import type { MedplumClient } from '@medplum/core';
import type { QuestionnaireResponse } from '@medplum/fhirtypes';
import { APPOINTMENT_TAG_SYSTEM } from '@unify/fhir-model/constants';
import {
  clinicalNoteLockSlice,
  signedNoteBlocksAppointmentEdit,
} from '@unify/fhir-model/resources/questionnaire-response';

export const SIGNED_NOTE_APPOINTMENT_LOCK_MESSAGE =
  'This appointment is locked because the linked clinical note is signed.';

/** True when at least one clinical note is linked to the appointment. */
export function appointmentNotesRequireScheduleBot(
  notes: readonly QuestionnaireResponse[],
): boolean {
  return notes.length > 0;
}

async function fetchAppointmentNotes(
  medplum: Pick<MedplumClient, 'searchResources'>,
  appointmentId: string,
  count: string,
): Promise<QuestionnaireResponse[]> {
  return (await medplum.searchResources('QuestionnaireResponse', {
    _tag: `${APPOINTMENT_TAG_SYSTEM}|${appointmentId}`,
    _count: count,
  })) as QuestionnaireResponse[];
}

/** Look up notes tagged to the appointment. Failures route through the bot (fail closed). */
export async function appointmentRequiresScheduleBot(
  medplum: Pick<MedplumClient, 'searchResources'>,
  appointmentId: string,
): Promise<boolean> {
  try {
    return appointmentNotesRequireScheduleBot(await fetchAppointmentNotes(medplum, appointmentId, '1'));
  } catch (err) {
    console.warn('[signedNoteAppointmentLock] Note lookup failed; routing through bot:', err);
    return true;
  }
}

/**
 * Guard for the one write that cannot go through the bot: drag-reschedule of a room-less
 * appointment, which PUTs `start`/`end` directly. Re-reads the notes at write time rather
 * than trusting the calendar's cached `linkedNoteSigned` flag. Throws when locked, and also
 * when the lookup fails (fail closed).
 */
export async function assertAppointmentUnlockedForDirectWrite(
  medplum: Pick<MedplumClient, 'searchResources'>,
  appointmentId: string,
  actorPractitionerId: string | undefined,
): Promise<void> {
  const notes = await fetchAppointmentNotes(medplum, appointmentId, '20');
  if (signedNoteBlocksAppointmentEdit(notes.map(clinicalNoteLockSlice), actorPractitionerId)) {
    throw new Error(SIGNED_NOTE_APPOINTMENT_LOCK_MESSAGE);
  }
}
